import type { GameCardProps } from "@/components/layout/GameCard";
import type { TrainingClass, TrainingSession } from "@/types/training";
import { getChargedAmount } from "@/utils/payment.utils";
import {
  formatTrainingDate,
  formatTrainingRecurrencePreview,
  formatTrainingScheduleSummary,
  getTrainingClassCoverImage,
  getTrainingSessionCoverImage,
  TRAINING_CT_BADGE,
  TRAINING_EVENT_BADGE,
} from "@/utils/training.utils";

type TrainingCardRegistration = {
  isRegistered?: boolean;
  userEnrollmentStatus?: string | null;
  paymentExpiresAt?: string | null;
};

function applyTrainingRegistrationToCard(
  base: GameCardProps,
  registration?: TrainingCardRegistration,
): GameCardProps {
  if (!registration?.isRegistered) return base;

  const now = new Date();
  const userStatus = registration.userEnrollmentStatus ?? null;

  if (userStatus === "PENDING_PAYMENT") {
    let timeLeft: string | undefined;
    let countdown: GameCardProps["countdown"];

    const paymentExpiresAt = registration.paymentExpiresAt;
    if (paymentExpiresAt) {
      const expiresAt = new Date(paymentExpiresAt);
      if (expiresAt > now) {
        const diff = expiresAt.getTime() - now.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        timeLeft =
          hours > 0 ? `${hours}h ${minutes}m restantes` : `${minutes}m restantes`;
        countdown = {
          days: 0,
          hours,
          minutes,
          seconds: Math.floor((diff % (1000 * 60)) / 1000),
        };
      }
    }

    return {
      ...base,
      status: "payment-due",
      statusLabel: "Pagamento Pendente",
      statusVariant: "warning",
      timeLeft,
      countdown,
      actionLabel: "Continuar",
      actionVariant: "primary",
      actionIcon: undefined,
    };
  }

  if (userStatus === "CONFIRMED") {
    return {
      ...base,
      status: "just-added",
      statusLabel: "Inscrito",
      statusVariant: "success",
      actionLabel: "Ver detalhes",
      actionVariant: "primary",
      actionIcon: undefined,
    };
  }

  return base;
}

function classRecurrenceLabel(trainingClass: TrainingClass): string {
  return (
    formatTrainingRecurrencePreview(
      trainingClass.scheduleDays,
      trainingClass.scheduleTime,
      trainingClass.startsOn,
    ) ??
    formatTrainingScheduleSummary(
      trainingClass.scheduleDays,
      trainingClass.scheduleTime,
      trainingClass.startsOn,
    ) ??
    "Horário recorrente"
  );
}

export function trainingSessionToCardProps(
  session: TrainingSession,
  onAction?: () => void,
  registration?: TrainingCardRegistration,
): GameCardProps {
  const confirmed = session.confirmedCount ?? 0;
  const max = session.maxParticipants ?? 0;
  const isFull = session.status === "FULL" || (max > 0 && confirmed >= max);
  const basePrice = Number(session.pricePerStudent ?? 0);
  const price = getChargedAmount(basePrice, "PIX", session.passFeesToUser ?? true);

  return applyTrainingRegistrationToCard(
    {
      id: session.id,
      title: session.title,
      image: getTrainingSessionCoverImage(session),
      date: formatTrainingDate(session.startsAt),
      location: session.location || session.team?.name || "Local a definir",
      sport: session.sport,
      currentPlayers: confirmed,
      maxPlayers: max,
      price,
      actionLabel: isFull ? "Ver evento" : "Inscrever-se",
      actionVariant: isFull ? "outline" : "primary",
      actionIcon: isFull ? undefined : "event",
      teamName: session.team?.name,
      teamId: session.team?.id,
      teamSlug: session.team?.slug,
      teamPhoto: session.team?.photo ?? undefined,
      entityLabel: TRAINING_EVENT_BADGE.label,
      entityIcon: TRAINING_EVENT_BADGE.icon,
      entityBadgeClassName: TRAINING_EVENT_BADGE.className,
      progressUnitLabel: "Inscrições",
      onAction,
      status: isFull ? "full" : "just-added",
      statusLabel: isFull ? "Lotado" : "Inscrições abertas",
      statusVariant: isFull ? "neutral" : "success",
    },
    registration,
  );
}

export function trainingClassToCardProps(
  trainingClass: TrainingClass,
  onAction?: () => void,
  registration?: TrainingCardRegistration,
): GameCardProps {
  const enrolled = trainingClass.enrollmentCount ?? 0;
  const max = trainingClass.maxStudents ?? 0;
  const isFull = max > 0 && enrolled >= max;
  const basePrice =
    trainingClass.pricingType === "VIP_INCLUDED" ? 0 : Number(trainingClass.monthlyPrice ?? 0);
  const price = basePrice > 0 ? getChargedAmount(basePrice, "PIX", true) : 0;

  return applyTrainingRegistrationToCard(
    {
      id: trainingClass.id,
      title: trainingClass.name,
      image: getTrainingClassCoverImage(trainingClass),
      date: classRecurrenceLabel(trainingClass),
      location: trainingClass.location || trainingClass.team?.name || "Local a definir",
      sport: trainingClass.sport,
      currentPlayers: enrolled,
      maxPlayers: max,
      price,
      actionLabel: isFull ? "Ver CT" : "Ver detalhes",
      actionVariant: isFull ? "outline" : "primary",
      teamName: trainingClass.team?.name,
      teamId: trainingClass.teamId ?? trainingClass.team?.id,
      teamSlug: trainingClass.team?.slug,
      teamPhoto: trainingClass.team?.photo ?? undefined,
      entityLabel: TRAINING_CT_BADGE.label,
      entityIcon: TRAINING_CT_BADGE.icon,
      entityBadgeClassName: TRAINING_CT_BADGE.className,
      progressUnitLabel: "Matrículas",
      onAction,
      status: isFull ? "full" : "interest-open",
      statusLabel: isFull ? "Lotado" : "Matrículas abertas",
      statusVariant: isFull ? "neutral" : "success",
    },
    registration,
  );
}
